import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Badge } from 'react-bootstrap';
import { MdDownload, MdPrint, MdEmail, MdFilterList, MdDateRange } from 'react-icons/md';

const Reports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState('');

  const reportTemplates = [
    { id: 'sales', name: 'Sales Report', description: 'Monthly sales performance and trends', category: 'Financial' },
    { id: 'inventory', name: 'Inventory Report', description: 'Stock levels and movement analysis', category: 'Operational' },
    { id: 'customers', name: 'Customer Report', description: 'Customer demographics and behavior', category: 'Analytics' },
    { id: 'orders', name: 'Order Report', description: 'Order processing and fulfillment status', category: 'Operational' },
    { id: 'revenue', name: 'Revenue Report', description: 'Revenue breakdown by product/service', category: 'Financial' },
    { id: 'performance', name: 'Performance Report', description: 'Key performance indicators and metrics', category: 'Analytics' }
  ];

  const recentReports = [
    { id: 1, name: 'Sales Report - January 2025', type: 'Financial', generated: '2025-01-15', status: 'Completed' },
    { id: 2, name: 'Inventory Report - January 2025', type: 'Operational', generated: '2025-01-14', status: 'Completed' },
    { id: 3, name: 'Customer Report - Q4 2024', type: 'Analytics', generated: '2025-01-10', status: 'Completed' },
    { id: 4, name: 'Revenue Report - December 2024', type: 'Financial', generated: '2025-01-05', status: 'Completed' }
  ];

  const getStatusBadge = (status: string) => {
    return status === 'Completed' ? 
      <Badge bg="success">{status}</Badge> : 
      <Badge bg="warning">{status}</Badge>;
  };

  const getCategoryBadge = (category: string) => {
    const variants: { [key: string]: string } = {
      'Financial': 'primary',
      'Operational': 'success',
      'Analytics': 'info'
    };
    return <Badge bg={variants[category] || 'secondary'}>{category}</Badge>;
  };

  return (
    <Container fluid className="py-4">
      <div className="page-header mb-4">
        <h2>Report Center</h2>
        <p className="text-muted">Generate and manage business reports</p>
      </div>

      <Row className="mb-4">
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Report Templates</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                {reportTemplates.map((template) => (
                  <Col md={6} lg={4} key={template.id} className="mb-3">
                    <Card 
                      className={`h-100 cursor-pointer ${selectedReport === template.id ? 'border-primary' : ''}`}
                      onClick={() => setSelectedReport(template.id)}
                    >
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="card-title">{template.name}</h6>
                          {getCategoryBadge(template.category)}
                        </div>
                        <p className="card-text text-muted small">{template.description}</p>
                        <Button 
                          variant={selectedReport === template.id ? 'primary' : 'outline-primary'} 
                          size="sm"
                          className="w-100"
                        >
                          Generate Report
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Quick Actions</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button variant="outline-primary">
                  <MdDateRange className="me-2" />
                  Custom Date Range
                </Button>
                <Button variant="outline-secondary">
                  <MdFilterList className="me-2" />
                  Advanced Filters
                </Button>
                <Button variant="outline-success">
                  <MdDownload className="me-2" />
                  Export All
                </Button>
                <Button variant="outline-info">
                  <MdEmail className="me-2" />
                  Email Reports
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recent Reports</h5>
              <div>
                <Button variant="outline-secondary" size="sm" className="me-2">
                  <MdPrint />
                </Button>
                <Button variant="outline-primary" size="sm">
                  <MdDownload />
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Report Name</th>
                    <th>Type</th>
                    <th>Generated</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReports.map((report) => (
                    <tr key={report.id}>
                      <td>{report.name}</td>
                      <td>{getCategoryBadge(report.type)}</td>
                      <td>{report.generated}</td>
                      <td>{getStatusBadge(report.status)}</td>
                      <td>
                        <Button variant="outline-primary" size="sm" className="me-1">
                          <MdDownload />
                        </Button>
                        <Button variant="outline-secondary" size="sm" className="me-1">
                          <MdPrint />
                        </Button>
                        <Button variant="outline-info" size="sm">
                          <MdEmail />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">24</h3>
              <p className="text-muted mb-0">Total Reports</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">18</h3>
              <p className="text-muted mb-0">Completed</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">4</h3>
              <p className="text-muted mb-0">In Progress</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">2</h3>
              <p className="text-muted mb-0">Scheduled</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Reports;
